import { useState, useEffect, useRef, MutableRefObject } from 'react'
import styles from '../TechPartnerDashboard/TechPartnerDashboard.module.scss'

import { TechPartnerDashboardInput } from '../TechPartnerDashboard/TechPartnerDashboard'

import { BUTTON_TYPES, AWCButton } from '../../GlobalComponents/Inputs/Inputs'
import DashboardDropDownList from '../../GlobalComponents/_misc/DashboardDropDownList'

import VendorMultiSelect from './MultiSelects/VendorMultiSelect'
import ProductCodeMultiSelect from './MultiSelects/ProductCodeMultiSelect'
import SalesTeamMultiSelect from './MultiSelects/SalesTeamMultiSelect'
import SalesReportGroupMultiSelect from './MultiSelects/SalesReportGroupMultiSelect'
import SalesReportAreaMultiSelect from './MultiSelects/SalesReportAreaMultiSelect'
import SalesDivisionMultiSelect from './MultiSelects/SalesDivisionMultiSelect'
import SalespersonMultiSelect from './MultiSelects/SalespersonMultiSelect'

import { VendorProductCodes, Vendors } from '../../features/inventory/inventoryAPI'
import {
    SalesDivision,
    Salesperson,
    SalesReportArea,
    SalesReportGroup,
    SalesTeam,
    fetchSalesGroups,
    FetchSalesGroupsReturnPayload,
} from '../../features/salesperson/salespersonAPI'

interface TechPartnerDashboardMainFilterInputsProps {
    selectedInputs: TechPartnerDashboardInput
    onSelectedValuesChanged(selectedInputs: TechPartnerDashboardInput): void
    onSelectedSalespeopleChanged(selectedSalespeople: Salesperson[]): void
    onSelectedSalesDivisionsChanged(selectedSalesDivisions: SalesDivision[]): void
    mainHeaderRef: MutableRefObject<null>
}

export const TechPartnerDashboardMainFilterInputs = ({
    onSelectedValuesChanged,
    onSelectedSalespeopleChanged,
    onSelectedSalesDivisionsChanged,
    mainHeaderRef,
}: TechPartnerDashboardMainFilterInputsProps) => {
    const [selectedVendors, setSelectedVendors] = useState<Vendors>([])
    const [selectedVendorProductCodes, setSelectedVendorProductCodes] =
        useState<VendorProductCodes>([])

    const [selectedSalesTeams, setSelectedSalesTeams] = useState<SalesTeam[]>([])
    const [selectedSalesReportGroups, setSelectedSalesReportGroups] = useState<SalesReportGroup[]>(
        []
    )
    const [selectedSalesReportAreas, setSelectedSalesReportAreas] = useState<SalesReportArea[]>([])
    const [selectedSalesDivisionsExplicit, setSelectedSalesDivisionsExplicit] = useState<
        SalesDivision[]
    >([])
    const [selectedSalespeople, setSelectedSalespeople] = useState<Salesperson[]>([])
    const [salespeopleFilter, setSalespeopleFilter] = useState<Salesperson[]>([])
    const [selectedSalesDivisionsImplicit, setSelectedSalesDivisionsImplicit] = useState<
        SalesDivision[]
    >([])

    const [selectedReportPeriodBegin, setSelectedReportPeriodBegin] = useState<Date>(
        new Date(new Date().getFullYear(), 0, 1)
    )
    // Report period gets whole months, so if the end is set to December, it should include all of December.
    // To make queries easier, the actual value will be 1/1 of the following month
    const firstOfThisMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    const [selectedReportPeriodEnd, setSelectedReportPeriodEnd] = useState<Date>(firstOfThisMonth)
    // const [selectedFiscalYearBeginMonth, setSelectedFiscalYearBeginMonth] = useState<Month>({
    //     Name: 'January',
    //     Number: 1,
    // })

    const [salesDivisionData, setSalesDivisionData] = useState<SalesDivision[]>([])
    const [salespeopleData, setSalespeopleData] = useState<Salesperson[]>([])

    const [selectedInputsChanged, setSelectedInputsChanged] = useState<boolean>(true)
    const [selectedInputsAreValid, setSelectedInputsAreValid] = useState<boolean>(true)
    const [prevSelectedInputs, setPrevSelectedInputs] = useState<TechPartnerDashboardInput>({})

    const reportPeriodRef = useRef<HTMLDivElement | null>(null)

    type Month = {
        Name: string
        Number: number
    }

    const months: Month[] = new Array(12).fill(0).map((_, i) => {
        return {
            Name: new Date(`${i + 1}/1`).toLocaleDateString(undefined, { month: 'long' }),
            Number: i + 1,
        }
    })

    const years: number[] = new Array(2).fill(0).map((_, i) => {
        return new Date().getFullYear() - i
    })

    useEffect(() => {
        async function getSalesGroups() {
            let SGs: FetchSalesGroupsReturnPayload = await fetchSalesGroups()
            if (SGs) {
                setSalesDivisionData(SGs.IncomeStatementGroups)
                setSalespeopleData(SGs.SalesPerson)
            }
        }
        getSalesGroups()
    }, [])

    useEffect(() => {
        const selectedInputs: TechPartnerDashboardInput = {
            VendorProductCodes: selectedVendorProductCodes,
            SalespersonCodes: salespeopleFilter,
            SalesDivisions: selectedSalesDivisionsImplicit,
            ReportPeriodBegin: selectedReportPeriodBegin,
            ReportPeriodEnd: ReportPeriodEndForQuery(),
        }

        setSelectedInputsChanged(
            JSON.stringify(prevSelectedInputs) !== JSON.stringify(selectedInputs)
        )
    }, [
        prevSelectedInputs,
        selectedVendorProductCodes,
        salespeopleFilter,
        selectedReportPeriodBegin,
        selectedReportPeriodEnd,
    ])

    useEffect(() => {
        onSelectedSalespeopleChanged(selectedSalespeople)
    }, [selectedSalespeople])

    useEffect(() => {
        onSelectedSalesDivisionsChanged(selectedSalesDivisionsImplicit)
    }, [selectedSalesDivisionsImplicit])

    const submitDashboard = () => {
        const inputs: TechPartnerDashboardInput = {
            VendorProductCodes: selectedVendorProductCodes,
            SalespersonCodes: salespeopleFilter,
            SalesDivisions: selectedSalesDivisionsImplicit,
            ReportPeriodBegin: selectedReportPeriodBegin,
            ReportPeriodEnd: ReportPeriodEndForQuery(),
        }
        setPrevSelectedInputs(inputs)
        onSelectedValuesChanged(inputs)
    }

    const ReportPeriodEndForQuery = (): Date => {
        return new Date(
            selectedReportPeriodEnd.getFullYear(),
            selectedReportPeriodEnd.getMonth() + 1,
            1
        )
    }

    enum FILTERTYPE {
        TEAM = 1,
        REPORTGROUP = 2,
        REPORTAREA = 3,
        DIVISION = 4,
        SALESPERSON = 5,
    }

    // decides which sales people to query for base on params chosen by user
    const updateSalespeopleFilter = (filterType: FILTERTYPE, selectedData: any[]) => {
        // specific salespeople chosen - query for them directly
        let selectedSPData =
            filterType === FILTERTYPE.SALESPERSON ? selectedData : selectedSalespeople

        // figure out which divisions as chosen by team/sales group/sales area
        let divisionData: SalesDivision[] = []
        if (selectedSPData.length === 0) {
            // divisions chosen directly - use salespeople in those divisions
            divisionData =
                filterType === FILTERTYPE.DIVISION ? selectedData : selectedSalesDivisionsExplicit

            if (divisionData.length === 0) {
                // if no divisions chosen directly use all divisions belonging to chosen report areas
                let reportAreaData: SalesReportArea[] =
                    filterType === FILTERTYPE.REPORTAREA ? selectedData : selectedSalesReportAreas
                divisionData = salesDivisionData.filter((d) =>
                    reportAreaData.some(
                        (ra) =>
                            ra.Team === d.Team &&
                            ra.ReportGroup === d.ReportGroup &&
                            ra.ReportArea === d.ReportArea
                    )
                )
            }

            if (divisionData.length === 0) {
                // if no report areas chosen use all divisions belonging to chosen report groups
                let reportGroupData: SalesReportGroup[] =
                    filterType === FILTERTYPE.REPORTGROUP ? selectedData : selectedSalesReportGroups
                divisionData = salesDivisionData.filter((d) =>
                    reportGroupData.some(
                        (rg) => rg.Team === d.Team && rg.ReportGroup === d.ReportGroup
                    )
                )
            }

            if (divisionData.length === 0) {
                // if no report groups chosen use all divisions belonging to chosen teams
                let teamData: SalesTeam[] =
                    filterType === FILTERTYPE.TEAM ? selectedData : selectedSalesTeams
                divisionData = salesDivisionData.filter((d) =>
                    teamData.some((t) => t.Team === d.Team)
                )
            }

            // query for all salespeople in divisions chosen above
            selectedSPData = salespeopleData.filter((sp) =>
                divisionData.some((div) => div.Division === sp.SalespersonCode.substring(0, 2))
            )
        }

        setSalespeopleFilter(selectedSPData)
        setSelectedSalesDivisionsImplicit(divisionData)
    }

    const reportPeriodIsValid = (
        selectedReportPeriodBegin: Date,
        selectedReportPeriodEnd: Date,
        errorCssClass: string
    ) => {
        if (!reportPeriodRef.current) return

        if (selectedReportPeriodEnd < selectedReportPeriodBegin) {
            reportPeriodRef.current?.classList.add(errorCssClass)
            setSelectedInputsAreValid(false)
            return false
        } else {
            reportPeriodRef.current?.classList.remove(errorCssClass)
            setSelectedInputsAreValid(true)
            return true
        }
    }

    return (
        <div
            className={`${styles.mainInputs} ${styles.sectionContainer}`}
            id='main-header'
            ref={mainHeaderRef}
        >
            <div className={styles.vendorFilter}>
                <div className={styles.inputTitle}>
                    Tech Partner{selectedVendors.length > 0 && ` (${selectedVendors.length})`}
                </div>
                <div className={styles.input}>
                    <VendorMultiSelect
                        selectedValues={selectedVendors}
                        onSelectedValuesChanged={(selectedValues: Vendors) => {
                            setSelectedVendors(selectedValues)
                        }}
                    />
                </div>
            </div>
            <div className={styles.separator} />
            <div className={styles.teamFilter}>
                <div className={styles.inputTitle}>
                    Team{selectedSalesTeams.length > 0 && ` (${selectedSalesTeams.length})`}
                </div>
                <div className={styles.input}>
                    <SalesTeamMultiSelect
                        data={salesDivisionData}
                        selectedValues={selectedSalesTeams}
                        onSelectedValuesChanged={(selectedValues: SalesTeam[]) => {
                            setSelectedSalesTeams(selectedValues)
                            updateSalespeopleFilter(FILTERTYPE.TEAM, selectedValues)
                        }}
                    />
                </div>
            </div>
            <div className={styles.vpFilter}>
                <div className={styles.inputTitle}>
                    VP
                    {selectedSalesReportGroups.length > 0 &&
                        ` (${selectedSalesReportGroups.length})`}
                </div>
                <div className={styles.input}>
                    <SalesReportGroupMultiSelect
                        data={salesDivisionData}
                        selectedSalesTeams={selectedSalesTeams}
                        selectedValues={selectedSalesReportGroups}
                        onSelectedValuesChanged={(selectedValues: SalesReportGroup[]) => {
                            setSelectedSalesReportGroups(selectedValues)
                            updateSalespeopleFilter(FILTERTYPE.REPORTGROUP, selectedValues)
                        }}
                    />
                </div>
            </div>
            <div className={styles.productCodeFilter}>
                <div className={styles.inputTitle}>
                    Product Code
                    {selectedVendorProductCodes.length > 0 &&
                        ` (${selectedVendorProductCodes.length})`}
                </div>
                <div className={styles.input}>
                    <ProductCodeMultiSelect
                        selectedValues={selectedVendorProductCodes}
                        onSelectedValuesChanged={(selectedValues: VendorProductCodes) => {
                            setSelectedVendorProductCodes(selectedValues)
                        }}
                        selectedVendors={selectedVendors}
                    />
                </div>
            </div>
            <div className={styles.reportAreaFilter}>
                <div className={styles.inputTitle}>
                    Report Area
                    {selectedSalesReportAreas.length > 0 && ` (${selectedSalesReportAreas.length})`}
                </div>
                <div className={styles.input}>
                    <SalesReportAreaMultiSelect
                        data={salesDivisionData}
                        selectedSalesReportGroups={selectedSalesReportGroups}
                        selectedValues={selectedSalesReportAreas}
                        onSelectedValuesChanged={(selectedValues: SalesReportArea[]) => {
                            setSelectedSalesReportAreas(selectedValues)
                            updateSalespeopleFilter(FILTERTYPE.REPORTAREA, selectedValues)
                        }}
                    />
                </div>
            </div>
            <div className={styles.reportPeriod} ref={reportPeriodRef}>
                <div className={styles.inputTitle}>Report Period</div>
                <div className={styles.errorMessage}>
                    End Month/Year must occur after Begin Month/Year
                </div>
                <div className={`${styles.secondaryTitle} ${styles.beginMonthTitle}`}>
                    Begin Month
                </div>
                <div className={styles.beginMonth}>
                    <DashboardDropDownList
                        data={months}
                        textField='Name'
                        keyField='Number'
                        selectedValue={months[selectedReportPeriodBegin.getMonth()]}
                        onSelectedValueChanged={(selectedValue: Month) => {
                            const newSelectedReportPeriodBegin = new Date(
                                selectedReportPeriodBegin.getFullYear(),
                                selectedValue.Number - 1,
                                1
                            )

                            if (
                                !reportPeriodIsValid(
                                    newSelectedReportPeriodBegin,
                                    selectedReportPeriodEnd,
                                    styles.error
                                )
                            )
                                return

                            setSelectedReportPeriodBegin(newSelectedReportPeriodBegin)
                        }}
                    />
                </div>
                <div className={`${styles.secondaryTitle} ${styles.beginYearTitle}`}>
                    Begin Year
                </div>
                <div className={styles.beginYear}>
                    <DashboardDropDownList
                        data={years}
                        selectedValue={selectedReportPeriodBegin.getFullYear()}
                        onSelectedValueChanged={(selectedValue: number) => {
                            const newSelectedReportPeriodBegin = new Date(
                                selectedValue,
                                selectedReportPeriodBegin.getMonth(),
                                1
                            )
                            if (
                                !reportPeriodIsValid(
                                    newSelectedReportPeriodBegin,
                                    selectedReportPeriodEnd,
                                    styles.error
                                )
                            )
                                return

                            setSelectedReportPeriodBegin(newSelectedReportPeriodBegin)
                        }}
                    />
                </div>
                <div className={`${styles.secondaryTitle} ${styles.endMonthTitle}`}>End Month</div>
                <div className={styles.endMonth}>
                    <DashboardDropDownList
                        data={months}
                        textField='Name'
                        keyField='Number'
                        selectedValue={months[selectedReportPeriodEnd.getMonth()]}
                        onSelectedValueChanged={(selectedValue: Month) => {
                            const newSelectedReportPeriodEnd = new Date(
                                selectedReportPeriodEnd.getFullYear(),
                                selectedValue.Number - 1,
                                1
                            )
                            if (
                                !reportPeriodIsValid(
                                    selectedReportPeriodBegin,
                                    newSelectedReportPeriodEnd,
                                    styles.error
                                )
                            )
                                return

                            setSelectedReportPeriodEnd(newSelectedReportPeriodEnd)
                        }}
                    />
                </div>
                <div className={`${styles.secondaryTitle} ${styles.endYearTitle}`}>End Year</div>
                <div className={styles.endYear}>
                    <DashboardDropDownList
                        data={years}
                        selectedValue={selectedReportPeriodEnd.getFullYear()}
                        onSelectedValueChanged={(selectedValue: number) => {
                            const newSelectedReportPeriodEnd = new Date(
                                selectedValue,
                                selectedReportPeriodEnd.getMonth(),
                                1
                            )
                            if (
                                !reportPeriodIsValid(
                                    selectedReportPeriodBegin,
                                    newSelectedReportPeriodEnd,
                                    styles.error
                                )
                            )
                                return

                            setSelectedReportPeriodEnd(newSelectedReportPeriodEnd)
                        }}
                    />
                </div>
            </div>
            <div className={styles.divisionFilter}>
                <div className={styles.inputTitle}>
                    Division
                    {selectedSalesDivisionsExplicit.length > 0 &&
                        ` (${selectedSalesDivisionsExplicit.length})`}
                </div>
                <div className={styles.input}>
                    <SalesDivisionMultiSelect
                        data={salesDivisionData}
                        selectedSalesReportAreas={selectedSalesReportAreas}
                        selectedValues={selectedSalesDivisionsExplicit}
                        onSelectedValuesChanged={(selectedValues: SalesDivision[]) => {
                            setSelectedSalesDivisionsExplicit(selectedValues)
                            updateSalespeopleFilter(FILTERTYPE.DIVISION, selectedValues)
                        }}
                    />
                </div>
            </div>
            <div className={styles.salespersonFilter}>
                <div className={styles.inputTitle}>
                    Salesperson
                    {selectedSalespeople.length > 0 && ` (${selectedSalespeople.length})`}
                </div>
                <div className={styles.input}>
                    <SalespersonMultiSelect
                        data={salespeopleData}
                        selectedSalesDivisions={selectedSalesDivisionsExplicit}
                        selectedValues={selectedSalespeople}
                        onSelectedValuesChanged={(selectedValues: Salesperson[]) => {
                            setSelectedSalespeople(selectedValues)
                            setSalespeopleFilter(selectedValues)
                            updateSalespeopleFilter(FILTERTYPE.SALESPERSON, selectedValues)
                        }}
                    />
                </div>
            </div>
            <div className={`${styles.submitButton} export`}>
                <AWCButton
                    onClick={() => {
                        selectedInputsChanged && selectedInputsAreValid && submitDashboard()
                    }}
                    type={
                        selectedInputsChanged && selectedInputsAreValid
                            ? BUTTON_TYPES.PRIMARY
                            : BUTTON_TYPES.DISABLED
                    }
                >
                    Apply
                </AWCButton>
            </div>
        </div>
    )
}

export default TechPartnerDashboardMainFilterInputs
